import { mergeAttributes, Node, nodeInputRule, ResizableNodeView } from '@tiptap/core';
import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
/**
 * Matches an image to a ![image](src "title") on input.
 */
export const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;
function findImagePos(editor, imageTypeName) {
    var _a, _b, _c, _d;
    const sel = (_a = editor === null || editor === void 0 ? void 0 : editor.state) === null || _a === void 0 ? void 0 : _a.selection;
    if (!sel)
        return null;
    // NodeSelection on an image
    if (sel.node && ((_b = sel.node.type) === null || _b === void 0 ? void 0 : _b.name) === imageTypeName) {
        return typeof sel.from === 'number' ? sel.from : null;
    }
    // Cursor near an image (common when clicking toolbar)
    const $from = sel.$from;
    if (!$from)
        return null;
    const before = $from.nodeBefore;
    if (before && ((_c = before.type) === null || _c === void 0 ? void 0 : _c.name) === imageTypeName) {
        return $from.pos - before.nodeSize;
    }
    const after = $from.nodeAfter;
    if (after && ((_d = after.type) === null || _d === void 0 ? void 0 : _d.name) === imageTypeName) {
        return $from.pos;
    }
    return null;
}
function getBreakpoint(editor) {
    var _a, _b;
    return ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function getResponsiveConfig(options) {
    if (!options.responsive || !options.responsive.enabled)
        return null;
    return {
        prefix: options.responsive.prefix || 'vp',
        cssPropWidth: options.responsive.cssPropWidth || 'img-width',
        cssPropHeight: options.responsive.cssPropHeight || 'img-height',
        cssPropMarginLeft: options.responsive.cssPropMarginLeft || 'img-ml',
        cssPropMarginRight: options.responsive.cssPropMarginRight || 'img-mr',
        cssPropJustifyContent: options.responsive.cssPropJustifyContent || 'img-jc',
        resizeUnit: options.responsive.resizeUnit || 'px',
        percentBase: options.responsive.percentBase || 'parent',
    };
}
function getPercentBaseWidth(editor, el, mode) {
    var _a, _b, _c, _d, _e;
    if (mode === 'editor') {
        const dom = (_a = editor === null || editor === void 0 ? void 0 : editor.view) === null || _a === void 0 ? void 0 : _a.dom;
        const w = (dom === null || dom === void 0 ? void 0 : dom.clientWidth) || 0;
        return w || (((_b = el.parentElement) === null || _b === void 0 ? void 0 : _b.clientWidth) || 0);
    }
    return ((_c = el.parentElement) === null || _c === void 0 ? void 0 : _c.clientWidth) || (((_e = (_d = editor === null || editor === void 0 ? void 0 : editor.view) === null || _d === void 0 ? void 0 : _d.dom) === null || _e === void 0 ? void 0 : _e.clientWidth) || 0);
}
function toResponsiveSizeValue(px, unit, editor, el, percentBase) {
    if (unit === '%') {
        const base = getPercentBaseWidth(editor, el, percentBase);
        if (!base)
            return `${px}px`;
        const percent = (px / base) * 100;
        return `${Math.max(0, Math.min(100, +percent.toFixed(2)))}%`;
    }
    return `${px}px`;
}
/**
 * This extension allows you to insert images.
 * @see https://www.tiptap.dev/api/nodes/image
 */
export const Image = Node.create({
    name: 'image',
    addOptions() {
        return {
            inline: false,
            allowBase64: false,
            HTMLAttributes: {},
            resize: false,
            responsive: {
                enabled: true,
                prefix: 'vp',
                cssPropWidth: 'img-width',
                cssPropHeight: 'img-height',
                cssPropMarginLeft: 'img-ml',
                cssPropMarginRight: 'img-mr',
                cssPropJustifyContent: 'img-jc',
                resizeUnit: 'px',
                percentBase: 'parent',
            },
        };
    },
    inline() {
        return this.options.inline;
    },
    group() {
        return this.options.inline ? 'inline' : 'block';
    },
    draggable: true,
    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            width: {
                default: null,
            },
            height: {
                default: null,
            },
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    },
    parseMarkdown: (token, helpers) => {
        return helpers.createNode('image', {
            src: token.href,
            title: token.title,
            alt: token.text,
        });
    },
    renderMarkdown: node => {
        var _a, _b, _c, _d, _e, _f;
        const src = (_b = (_a = node.attrs) === null || _a === void 0 ? void 0 : _a.src) !== null && _b !== void 0 ? _b : '';
        const alt = (_d = (_c = node.attrs) === null || _c === void 0 ? void 0 : _c.alt) !== null && _d !== void 0 ? _d : '';
        const title = (_f = (_e = node.attrs) === null || _e === void 0 ? void 0 : _e.title) !== null && _f !== void 0 ? _f : '';
        return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`;
    },
    addNodeView() {
        if (!this.options.resize || !this.options.resize.enabled || typeof document === 'undefined') {
            return null;
        }
        const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = this.options.resize;
        return ({ node, getPos, HTMLAttributes, editor }) => {
            var _a;
            const el = document.createElement('img');
            // Track which resize handle is currently active so we can lock aspect ratio only on corners.
            let activeHandle = null;
            // Cache the intrinsic aspect ratio of the image (width / height)
            let aspectRatio = null;
            const isCornerHandle = (dir) => {
                return dir === 'top-left'
                    || dir === 'top-right'
                    || dir === 'bottom-left'
                    || dir === 'bottom-right';
            };
            const ensureAspectRatio = () => {
                if (aspectRatio)
                    return aspectRatio;
                // Prefer natural sizes if available
                if (el.naturalWidth && el.naturalHeight) {
                    aspectRatio = el.naturalWidth / el.naturalHeight;
                    return aspectRatio;
                }
                // Fallback to current box
                const rect = el.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    aspectRatio = rect.width / rect.height;
                    return aspectRatio;
                }
                return null;
            };
            Object.entries(HTMLAttributes).forEach(([key, value]) => {
                if (value != null) {
                    switch (key) {
                        case 'width':
                        case 'height':
                            break;
                        default:
                            el.setAttribute(key, value);
                            break;
                    }
                }
            });
            el.src = HTMLAttributes.src;
            if (HTMLAttributes.style) {
                el.setAttribute('style', HTMLAttributes.style);
            }
            // Will be assigned after ResizableNodeView is created.
            let containerEl = null;
            const syncVarsToContainer = (styleString) => {
                if (!containerEl)
                    return;
                const cfg = getResponsiveConfig(this.options);
                const prefix = (cfg === null || cfg === void 0 ? void 0 : cfg.prefix) || 'vp';
                // Remove previously applied vp vars so switching breakpoints doesn't leave stale values.
                for (let i = containerEl.style.length - 1; i >= 0; i--) {
                    const name = containerEl.style.item(i);
                    if (name && name.startsWith(`--${prefix}-`)) {
                        containerEl.style.removeProperty(name);
                    }
                }
                const obj = parseStyleString(styleString || '');
                Object.entries(obj).forEach(([k, v]) => {
                    if (k.startsWith(`--${prefix}-`)) {
                        containerEl.style.setProperty(k, v);
                    }
                });
            };
            // Initial sync from HTML to container (editor-only)
            if (HTMLAttributes.style) {
                syncVarsToContainer(HTMLAttributes.style);
            }
            const nodeView = new ResizableNodeView({
                element: el,
                editor,
                node,
                getPos,
                onResize: (width, height) => {
                    const cfg = getResponsiveConfig(this.options);
                    // If a corner handle is active, keep the aspect ratio (px mode only).
                    const ratio = ensureAspectRatio();
                    if (ratio && isCornerHandle(activeHandle)) {
                        height = Math.round(width / ratio);
                    }
                    // default preview (non-responsive)
                    if (!cfg) {
                        el.style.width = `${width}px`;
                        el.style.height = `${height}px`;
                        return;
                    }
                    const bp = getBreakpoint(editor);
                    const wVar = responsiveVarName(cfg.cssPropWidth, bp, cfg.prefix);
                    const hVar = responsiveVarName(cfg.cssPropHeight, bp, cfg.prefix);
                    const widthValue = toResponsiveSizeValue(width, cfg.resizeUnit, editor, el, cfg.percentBase);
                    // Live preview vars during drag
                    el.style.setProperty(wVar, widthValue);
                    if (containerEl)
                        containerEl.style.setProperty(wVar, widthValue);
                    if (cfg.resizeUnit === '%') {
                        el.style.setProperty(hVar, 'auto');
                        if (containerEl)
                            containerEl.style.setProperty(hVar, 'auto');
                    }
                    else {
                        el.style.setProperty(hVar, `${height}px`);
                        if (containerEl)
                            containerEl.style.setProperty(hVar, `${height}px`);
                    }
                    // Ensure CSS vars remain the source of truth
                    el.style.removeProperty('width');
                    el.style.removeProperty('height');
                },
                onCommit: (width, height) => {
                    var _a, _b, _c, _d;
                    const pos = getPos();
                    if (pos === undefined) {
                        return;
                    }
                    const ratio = ensureAspectRatio();
                    if (ratio && isCornerHandle(activeHandle)) {
                        height = Math.round(width / ratio);
                    }
                    const cfg = getResponsiveConfig(this.options);
                    // default commit (non-responsive)
                    if (!cfg) {
                        this.editor
                            .chain()
                            .setNodeSelection(pos)
                            .updateAttributes(this.name, {
                            width,
                            height,
                        })
                            .run();
                        return;
                    }
                    const bp = getBreakpoint(editor);
                    const wVar = responsiveVarName(cfg.cssPropWidth, bp, cfg.prefix);
                    const hVar = responsiveVarName(cfg.cssPropHeight, bp, cfg.prefix);
                    // IMPORTANT: use latest node attrs from editor state
                    // @ts-ignore
                    const latestNode = (_a = editor.state.selection) === null || _a === void 0 ? void 0 : _a.node;
                    const currentStyle = (latestNode && ((_b = latestNode.type) === null || _b === void 0 ? void 0 : _b.name) === this.name)
                        ? (_c = latestNode.attrs) === null || _c === void 0 ? void 0 : _c.style
                        : (_d = node.attrs) === null || _d === void 0 ? void 0 : _d.style;
                    const styleObj = parseStyleString(currentStyle || '');
                    // remove any raw stored width/height from style
                    delete styleObj.width;
                    delete styleObj.height;
                    // set responsive vars
                    styleObj[wVar] = toResponsiveSizeValue(width, cfg.resizeUnit, editor, el, cfg.percentBase);
                    styleObj[hVar] = cfg.resizeUnit === '%' ? 'auto' : `${height}px`;
                    const nextStyle = stringifyStyleObject(styleObj);
                    this.editor
                        .chain()
                        .setNodeSelection(pos)
                        .updateAttributes(this.name, {
                        style: nextStyle,
                        // keep schema clean so responsive vars are the single source of truth
                        width: null,
                        height: null,
                    })
                        .run();
                },
                onUpdate: (updatedNode, _decorations, _innerDecorations) => {
                    var _a, _b, _c, _d;
                    if (updatedNode.type !== node.type) {
                        return false;
                    }
                    // Keep DOM in sync with attrs (alignment + responsive vars live in `style`)
                    const nextStyle = ((_a = updatedNode.attrs) === null || _a === void 0 ? void 0 : _a.style) || '';
                    if (nextStyle) {
                        el.setAttribute('style', nextStyle);
                    }
                    else {
                        el.removeAttribute('style');
                    }
                    // Keep resize container in sync for correct selected-state layout
                    syncVarsToContainer(nextStyle);
                    // Also keep src/alt/title in sync if changed
                    if ((_b = updatedNode.attrs) === null || _b === void 0 ? void 0 : _b.src)
                        el.src = updatedNode.attrs.src;
                    if (((_c = updatedNode.attrs) === null || _c === void 0 ? void 0 : _c.alt) != null)
                        el.setAttribute('alt', updatedNode.attrs.alt);
                    if (((_d = updatedNode.attrs) === null || _d === void 0 ? void 0 : _d.title) != null)
                        el.setAttribute('title', updatedNode.attrs.title);
                    return true;
                },
                options: {
                    directions,
                    min: {
                        width: minWidth,
                        height: minHeight,
                    },
                    // We handle aspect ratio ourselves on corner handles only.
                    // Keep this `false` so edges can resize freely (unless user holds Shift, which the core supports).
                    preserveAspectRatio: false,
                },
            });
            // Detect active handle: the core renders handles with `data-resize-handle`.
            // We store the active direction so we can lock aspect ratio on corners only.
            const dom = nodeView.dom;
            // Assign the container element pointer for responsive var sync
            containerEl = dom;
            // Ensure the container has the latest vars (editor-only)
            const initialStyle = (node && node.attrs && node.attrs.style) ? node.attrs.style : (HTMLAttributes.style || '');
            syncVarsToContainer(initialStyle);
            const onPointerUp = () => {
                activeHandle = null;
            };
            // Use pointer events to support mouse + touch.
            window.addEventListener('pointerup', onPointerUp);
            dom.querySelectorAll('[data-resize-handle]').forEach(handleEl => {
                handleEl.addEventListener('pointerdown', (event) => {
                    const target = event.currentTarget;
                    const dir = target === null || target === void 0 ? void 0 : target.getAttribute('data-resize-handle');
                    activeHandle = dir;
                    // Ensure aspect ratio is cached as soon as user starts resizing.
                    ensureAspectRatio();
                });
            });
            // Prevent event listener leaks
            const originalDestroy = (_a = nodeView.destroy) === null || _a === void 0 ? void 0 : _a.bind(nodeView);
            nodeView.destroy = () => {
                window.removeEventListener('pointerup', onPointerUp);
                originalDestroy === null || originalDestroy === void 0 ? void 0 : originalDestroy();
            };
            // when image is loaded, show the node view to get the correct dimensions
            dom.style.visibility = 'hidden';
            dom.style.pointerEvents = 'none';
            el.onload = () => {
                dom.style.visibility = '';
                dom.style.pointerEvents = '';
            };
            return nodeView;
        };
    },
    // @ts-ignore
    addCommands() {
        return {
            setImage: options => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: options,
                });
            },
            setImageAlign: (align, opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const pos = findImagePos(editor, this.name);
                if (pos == null)
                    return false;
                const ch = chain().setNodeSelection(pos);
                // If responsive mode is disabled, fall back to non-responsive margins
                if (!cfg) {
                    const attrs = editor.getAttributes(this.name) || {};
                    const styleObj = parseStyleString(attrs.style || '');
                    // Clear raw margins first
                    delete styleObj['margin-left'];
                    delete styleObj['margin-right'];
                    if (align === 'left') {
                        styleObj['margin-left'] = '0';
                        styleObj['margin-right'] = 'auto';
                    }
                    else if (align === 'center') {
                        styleObj['margin-left'] = 'auto';
                        styleObj['margin-right'] = 'auto';
                    }
                    else if (align === 'right') {
                        styleObj['margin-left'] = 'auto';
                        styleObj['margin-right'] = '0';
                    }
                    return ch.updateAttributes(this.name, { ...attrs, style: stringifyStyleObject(styleObj) }).run();
                }
                const jcVar = responsiveVarName(cfg.cssPropJustifyContent, bp, cfg.prefix);
                const mlVar = responsiveVarName(cfg.cssPropMarginLeft, bp, cfg.prefix);
                const mrVar = responsiveVarName(cfg.cssPropMarginRight, bp, cfg.prefix);
                const attrs = editor.getAttributes(this.name) || {};
                const styleObj = parseStyleString(attrs.style || '');
                // Remove any raw margins so vars are the source of truth
                delete styleObj['margin-left'];
                delete styleObj['margin-right'];
                if (align === 'left') {
                    styleObj[jcVar] = 'left';
                    styleObj[mlVar] = '0';
                    styleObj[mrVar] = 'auto';
                }
                else if (align === 'center') {
                    styleObj[jcVar] = 'center';
                    styleObj[mlVar] = 'auto';
                    styleObj[mrVar] = 'auto';
                }
                else if (align === 'right') {
                    styleObj[jcVar] = 'right';
                    styleObj[mlVar] = 'auto';
                    styleObj[mrVar] = '0';
                }
                return ch
                    .updateAttributes(this.name, {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                })
                    .run();
            },
            unsetImageAlign: (opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const pos = findImagePos(editor, this.name);
                if (pos == null)
                    return false;
                const ch = chain().setNodeSelection(pos);
                const attrs = editor.getAttributes(this.name) || {};
                const styleObj = parseStyleString(attrs.style || '');
                if (!cfg) {
                    delete styleObj['margin-left'];
                    delete styleObj['margin-right'];
                    return ch.updateAttributes(this.name, { ...attrs, style: stringifyStyleObject(styleObj) }).run();
                }
                const jcVar = responsiveVarName(cfg.cssPropJustifyContent, bp, cfg.prefix);
                const mlVar = responsiveVarName(cfg.cssPropMarginLeft, bp, cfg.prefix);
                const mrVar = responsiveVarName(cfg.cssPropMarginRight, bp, cfg.prefix);
                delete styleObj[jcVar];
                delete styleObj[mlVar];
                delete styleObj[mrVar];
                return ch
                    .updateAttributes(this.name, {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                })
                    .run();
            },
            getImageAlign: (opts) => ({ editor }) => {
                var _a, _b;
                const cfg = getResponsiveConfig(this.options);
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const pos = findImagePos(editor, this.name);
                if (pos == null)
                    return null;
                const nodeAtPos = editor.state.doc.nodeAt(pos);
                const styleObj = parseStyleString((nodeAtPos && ((_a = nodeAtPos.type) === null || _a === void 0 ? void 0 : _a.name) === this.name ? (_b = nodeAtPos.attrs) === null || _b === void 0 ? void 0 : _b.style : '') || '');
                if (!cfg) {
                    const ml = styleObj['margin-left'] || null;
                    const mr = styleObj['margin-right'] || null;
                    if (ml === '0' && mr === 'auto')
                        return 'left';
                    if (ml === 'auto' && mr === 'auto')
                        return 'center';
                    if (ml === 'auto' && mr === '0')
                        return 'right';
                    return null;
                }
                const mlVar = responsiveVarName(cfg.cssPropMarginLeft, bp, cfg.prefix);
                const mrVar = responsiveVarName(cfg.cssPropMarginRight, bp, cfg.prefix);
                const ml = styleObj[mlVar] || null;
                const mr = styleObj[mrVar] || null;
                if (ml === '0' && mr === 'auto')
                    return 'left';
                if (ml === 'auto' && mr === 'auto')
                    return 'center';
                if (ml === 'auto' && mr === '0')
                    return 'right';
                return null;
            },
        };
    },
    addInputRules() {
        return [
            nodeInputRule({
                find: inputRegex,
                type: this.type,
                getAttributes: match => {
                    const [, , alt, src, title] = match;
                    return { src, alt, title };
                },
            }),
        ];
    },
});
